import React, { FC, PropsWithChildren, ReactNode } from 'react';

import { Helmet } from 'react-helmet-async';
import { DefaultTheme, ThemeProvider } from 'styled-components';

import { BaseStyle } from '@nhl/nhl-breakaway-ui';

import { useIsWebview } from '~/core/hooks/useIsWebview';
import AdTag from '~/views/components/adTag/AdTag';
import { AdType } from '~/views/components/adTag/enums';

import { DefaultPageBackground, DefaultPageContainer, TwoColumnPageContainer } from './styles';

type Props = PropsWithChildren & {
    defaultPageLayout?: boolean;
    hasExternalBackground?: boolean;
    pageTitle?: string;
    pageWrapperHeader?: ReactNode;
    theme: DefaultTheme;
    useSecondarySurfaceColorForBackground?: boolean;
};

const PageWrapper: FC<Props> = ({
    children,
    defaultPageLayout = false,
    hasExternalBackground = false,
    pageTitle,
    pageWrapperHeader,
    theme,
    useSecondarySurfaceColorForBackground = false,
}) => {
    const isWebview = useIsWebview();

    return (
        <ThemeProvider theme={theme}>
            <Helmet>{pageTitle ? <title>{pageTitle}</title> : ''}</Helmet>
            <BaseStyle.RootTheming>
                {defaultPageLayout ? (
                    <DefaultPageBackground $hasExternalBackground={hasExternalBackground}>
                        {/*
                            hasExternalBackground indicates that we are using the default template w/ background
                            from Deltatre. This template also loads the NHLHeaderAd include which will load these
                            ads for us. So rather than define them twice (in the include and here) we skip them here
                        */}
                        {!hasExternalBackground && (
                            <>
                                {window.__AD_SETTINGS__.takeoverDivId && (
                                    <AdTag divId={window.__AD_SETTINGS__.takeoverDivId} adType={AdType.TAKEOVER} />
                                )}
                                {window.__AD_SETTINGS__.headerDivId && (
                                    <AdTag divId={window.__AD_SETTINGS__.headerDivId} adType={AdType.HEADER} />
                                )}
                            </>
                        )}

                        {pageWrapperHeader}

                        <DefaultPageContainer
                            $hasExternalBackground={hasExternalBackground}
                            $useSecondarySurfaceColorForBackground={useSecondarySurfaceColorForBackground}
                            $isWebview={isWebview}
                        >
                            {children}
                        </DefaultPageContainer>
                    </DefaultPageBackground>
                ) : (
                    <TwoColumnPageContainer>{children}</TwoColumnPageContainer>
                )}
            </BaseStyle.RootTheming>
        </ThemeProvider>
    );
};

export default PageWrapper;
